import type { ReactNode } from 'react'
import cx from 'classnames'
import { ACCEPTED_OCR_FILE_TYPES } from '@/features/jobs/scoping/ocr-file-types'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
  ModalSheetFooterSpacer,
  ModalSheetContentBlock,
} from '@/components/ModalSheet'
import styles from './CreateOCRScopeJobsDialog.module.css'
import { IconButton } from '@/components/IconButton'
import { FileButton } from '@/components/FileButton'
import { Button } from '@/components/Button'
import { Form, FormGroup, FormRow } from '@/components/Form'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import { Spinner } from '@/components/Spinner'
import { Checkbox } from '@/components/Checkbox'
import { byDesign } from '@/infra/design'
import imgDragDropUrl from '@/assets/misc/img_drag-drop.svg'
import DragAndUploadIllustration from '@/components/SpotIllustration/DragAndUploadIllustration'
import type { ScopeJobMode } from './CreateOCRScopeJobsViewModel'

export interface CreateOCRScopeJobsDialogProps {
  showing: boolean
  isPending: boolean
  onClose: () => unknown
  children?: ReactNode
}

export function CreateOCRScopeJobsDialog({
  showing,
  isPending,
  onClose,
  children,
}: CreateOCRScopeJobsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      disableCloseOnOutsideClick
      onRequestClose={isPending ? undefined : onClose}
    >
      {children}
    </ModalSheet>
  )
}

export interface CreateOCRScopeJobsDialogManageDocumentsStepProps {
  selectedFilesCount: number
  onAddFiles: (files: FileList) => unknown
  onNext: () => unknown
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogManageDocumentsStep({
  selectedFilesCount,
  onAddFiles,
  onNext,
  children,
}: CreateOCRScopeJobsDialogManageDocumentsStepProps) {
  return (
    <>
      <CreateOCRScopeJobsDialogHeader
        heading="1 of 2"
        title="Manage documents"
        description="Review, add or remove files for job scoping"
      >
        <div className={styles.documentsColumnHeader}>
          <span className={styles.documentsColumnHeaderText}>Documents</span>
          <span className={styles.documentsColumnHeaderCount}>
            ({selectedFilesCount})
          </span>
        </div>
      </CreateOCRScopeJobsDialogHeader>
      <CreateOCRScopeJobsDialogManageDocumentsContent
        isEmpty={selectedFilesCount === 0}
      >
        {children}
      </CreateOCRScopeJobsDialogManageDocumentsContent>
      <ModalSheetFooter>
        <div className={styles.manageDocumentsFooterButtons}>
          <FileButton
            onChange={onAddFiles}
            multiple
            accept={ACCEPTED_OCR_FILE_TYPES}
          >
            <Button label="Add more" />
          </FileButton>
          <ModalSheetFooterSpacer />
          <Button
            label="Next"
            onPress={onNext}
            variant="primary"
            isDisabled={selectedFilesCount === 0}
          />
        </div>
      </ModalSheetFooter>
    </>
  )
}

export interface CreateOCRScopeJobsDialogScopeNewJobsStepProps {
  submitLabel: string
  isSubmitPending: boolean
  isDisabled: boolean
  onBack: () => unknown
  onSubmit: () => unknown
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogScopeNewJobsStep({
  submitLabel,
  isSubmitPending,
  isDisabled,
  onBack,
  onSubmit,
  children,
}: CreateOCRScopeJobsDialogScopeNewJobsStepProps) {
  return (
    <>
      <CreateOCRScopeJobsDialogHeader
        heading="2 of 2"
        title="Scope new jobs"
        description="Set the OCR requirements for the shared files"
      />
      {children}
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Back" isDisabled={isSubmitPending} onPress={onBack} />

          <Button
            label={submitLabel}
            variant="primary"
            onPress={onSubmit}
            isPending={isSubmitPending}
            isDisabled={isSubmitPending || isDisabled}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface CreateOCRScopeJobsDialogHeaderProps {
  heading: string
  title: string
  description: string
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogHeader({
  heading,
  title,
  description,
  children,
}: CreateOCRScopeJobsDialogHeaderProps) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.closeButtonContainer}>
          <ModalSheetHeaderCloseButton />
        </div>
        <div className={styles.headerContent}>
          <div className={styles.headerHeading}>{heading}</div>
          <div className={styles.headerTitle}>{title}</div>
          <div className={styles.headerDescription}>{description}</div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
}

export interface CreateOCRScopeJobsDialogManageDocumentsContentProps {
  isEmpty: boolean
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogManageDocumentsContent({
  isEmpty,
  children,
}: CreateOCRScopeJobsDialogManageDocumentsContentProps) {
  return (
    <ModalSheetContent>
      <div
        className={cx(
          styles.selectedFilesContainer,
          isEmpty && styles.noFilesSelected,
        )}
      >
        {isEmpty && (
          <div className={styles.selectedFilesEmptyStateContainer}>
            <div className={styles.selectedFilesEmptyStateText}>
              Please select a file to continue
            </div>
          </div>
        )}
        {children}
      </div>
    </ModalSheetContent>
  )
}

export interface CreateOCRScopeJobsDialogSelectedFileProps {
  name: string
  onRemove: () => unknown
}

export function CreateOCRScopeJobsDialogSelectedFile({
  name,
  onRemove,
}: CreateOCRScopeJobsDialogSelectedFileProps) {
  return (
    <div className={styles.selectedFile}>
      <div className={styles.selectedFileName}>{name}</div>
      <IconButton
        name="close"
        label="Remove file"
        className={styles.unselectFileButton}
        onPress={onRemove}
      />
    </div>
  )
}

export interface CreateOCRScopeJobsDialogScopeNewJobsFormProps {
  isFetchingManualQuestions: boolean
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogScopeNewJobsForm({
  isFetchingManualQuestions,
  children,
}: CreateOCRScopeJobsDialogScopeNewJobsFormProps) {
  return (
    <ModalSheetContent>
      <ModalSheetContentBlock>
        <Form>{children}</Form>
      </ModalSheetContentBlock>

      {isFetchingManualQuestions && (
        <div className={styles.manualQuestionsSpinner}>
          <Spinner />
        </div>
      )}
    </ModalSheetContent>
  )
}

export interface CreateOCRScopeJobsDialogScopeJobModeRowProps {
  showModeSelection: boolean
  scopeJobMode: ScopeJobMode
  onChangeScopeJobMode: (mode: ScopeJobMode) => unknown
}

export function CreateOCRScopeJobsDialogScopeJobModeRow({
  showModeSelection,
  scopeJobMode,
  onChangeScopeJobMode,
}: CreateOCRScopeJobsDialogScopeJobModeRowProps) {
  return (
    <FormRow>
      {showModeSelection && (
        <RadioButtonGroup
          size="big"
          label="How do you want to scope uploaded documents?"
        >
          <RadioButtonItem
            onPress={() => onChangeScopeJobMode('Collectively')}
            active={scopeJobMode === 'Collectively'}
          >
            Collectively
          </RadioButtonItem>
          <RadioButtonItem
            onPress={() => onChangeScopeJobMode('Individually')}
            active={scopeJobMode === 'Individually'}
          >
            Individually
          </RadioButtonItem>
        </RadioButtonGroup>
      )}
    </FormRow>
  )
}

export interface CreateOCRScopeJobsDialogManualQuestionsRowProps {
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogManualQuestionsRow({
  children,
}: CreateOCRScopeJobsDialogManualQuestionsRowProps) {
  return (
    <FormRow>
      <FormGroup>{children}</FormGroup>
    </FormRow>
  )
}

export interface CreateOCRScopeJobsDialogSaveAnswersRowProps {
  saveAnswers: boolean
  onToggleSaveAnswers: () => unknown
}

export function CreateOCRScopeJobsDialogSaveAnswersRow({
  saveAnswers,
  onToggleSaveAnswers,
}: CreateOCRScopeJobsDialogSaveAnswersRowProps) {
  return (
    <FormRow>
      <FormGroup>
        <div className={styles.saveAnswersContainer}>
          <Checkbox checked={saveAnswers} onChange={onToggleSaveAnswers} />
          <span className={styles.saveAnswersText}>
            Apply answers to all jobs
          </span>
        </div>
      </FormGroup>
    </FormRow>
  )
}

export interface CreateOCRScopeJobsDialogManualQuestionProps {
  title?: string
  children?: ReactNode
}

export function CreateOCRScopeJobsDialogManualQuestion({
  title,
  children,
}: CreateOCRScopeJobsDialogManualQuestionProps) {
  return (
    <>
      <div className={styles.questionTitle}>{title}</div>
      {children}
    </>
  )
}

export function CreateOCRScopeJobsDialogDraggingOverlay() {
  return (
    <div className={styles.dragAndDropOverlay}>
      {byDesign(
        <img alt="" className={styles.dragAndDropImage} src={imgDragDropUrl} />,
        <DragAndUploadIllustration className={styles.dragAndDropImage} />,
      )}
      <h2 className={styles.dragAndDropTitle}>
        Drag and drop to scope new jobs
      </h2>
      <p className={styles.dragAndDropDescription}>
        Upload one or more files here. Acceptable file types include: BMP, JPEG,
        JPG, PDF, PNG, TIF, TIFF, WEBP
      </p>
    </div>
  )
}
